"use client";

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import imageCompression from "browser-image-compression";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import { ServiceData } from "../../types/service";
import { allowedImageExtension } from "../../constants/constants";
import { CheckCircle, Loader, X } from "lucide-react";
import Header from "../common/header";
import Footer from "../common/footer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../lib/utils";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import BookingCheckout from "../booking/BookingCheckout";
import { CheckoutPageAnimator } from "../../components/checkout-page-animator";

// ----------------------
// Validation Schema
// ----------------------
const applicantSchema = z.object({
  files: z.object({
    photo: z.instanceof(File, { message: "Photo is required" }),
    passportCopy: z.instanceof(File, { message: "Passport copy is required" }),
  }),
});

const bookingDocumentSchema = z.object({
  applicants: z.array(applicantSchema),
});

type BookingDocumentForm = z.infer<typeof bookingDocumentSchema>;

// ----------------------
// File Upload
// ----------------------
const FileUpload = ({ form, applicantIndex, fieldName, label }: any) => {
  const file = form.watch(`applicants.${applicantIndex}.files.${fieldName}`);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setPreview(null);
    }
  }, [file]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      form.setValue(
        `applicants.${applicantIndex}.files.${fieldName}`,
        e.target.files[0]
      );
      // Clear any previous errors
      form.clearErrors(`applicants.${applicantIndex}.files.${fieldName}`);
    }
  };

  const removeFile = () => {
    form.setValue(`applicants.${applicantIndex}.files.${fieldName}`, undefined);
  };

  const error =
    form.formState.errors.applicants?.[applicantIndex]?.files?.[fieldName];

  return (
    <FormItem>
      <FormLabel className="text-sm font-medium">{label}</FormLabel>
      {preview && file ? (
        <div className="relative flex items-center gap-3 p-2 border rounded-md bg-gray-50/60">
          <div className="relative w-16 h-16 rounded-md overflow-hidden">
            {file.type.startsWith("image/") ? (
              <img
                src={preview}
                alt="preview"
                className="object-cover w-full h-full"
                style={{ position: "absolute", top: 0, left: 0 }}
              />
            ) : (
              <div className="flex items-center justify-center w-full h-full bg-gray-200">
                <X className="w-6 h-6 text-gray-500" />
              </div>
            )}
          </div>
          <div className="flex-grow overflow-hidden">
            <p className="text-sm font-medium truncate">{file.name}</p>
            <p className="text-xs text-muted-foreground">
              {(file.size / 1024).toFixed(2)} KB
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="rounded-full h-8 w-8"
            onClick={removeFile}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <FormControl>
          <Input
            type="file"
            onChange={handleFileChange}
            accept="image/*,application/pdf"
            className={cn(
              "pt-2 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 border-dashed border-2 p-2 h-auto cursor-pointer",
              error && "border-red-500"
            )}
          />
        </FormControl>
      )}
      {error && (
        <FormMessage className="text-red-500 text-xs mt-1">
          {error.message}
        </FormMessage>
      )}
    </FormItem>
  );
};

// ----------------------
// Main Component
// ----------------------
const BookingDocument = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { bookingStore, serviceStore } = useStore();
  const { uploadImage, getBooking, currentBooking } = bookingStore;
  const { getService } = serviceStore;

  const [service, setService] = useState<ServiceData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allFilesUploaded, setAllFilesUploaded] = useState(false);
  const [filesSubmitted, setFilesSubmitted] = useState(false);
  const [hasExistingFiles, setHasExistingFiles] = useState(false);

  const form = useForm<BookingDocumentForm>({
    resolver: zodResolver(bookingDocumentSchema),
    defaultValues: { applicants: [] },
  });

  const { control, handleSubmit, watch, formState } = form;
  const { fields } = useFieldArray({
    control,
    name: "applicants",
  });

  // Watch all form values to check file upload status
  const formValues = watch();

  // Check if all required files are uploaded for all applicants
  useEffect(() => {
    const checkAllFilesUploaded = () => {
      if (fields.length === 0) {
        setAllFilesUploaded(false);
        return;
      }

      const allUploaded = fields.every((_, applicantIndex) => {
        const applicantFiles =
          formValues.applicants?.[applicantIndex]?.files || {};

        // Check if both photo and passportCopy are uploaded for this applicant
        return applicantFiles.photo && applicantFiles.passportCopy;
      });

      setAllFilesUploaded(allUploaded);
    };

    checkAllFilesUploaded();
  }, [formValues, fields]);

  useEffect(() => {
    const fetchBookingData = async () => {
      if (!id) return;
      const booking = await getBooking(id);
      if (booking) {
        // Check if files already exist
        if (booking.fileEntities && booking.fileEntities.length > 0) {
          setHasExistingFiles(true);
          setFilesSubmitted(true);
          setAllFilesUploaded(true);
        }

        if (booking?.serviceId) {
          const svc = await getService(booking.serviceId);
          if (svc) {
            setService(svc);
            // Initialize with the correct number of applicants
            const applicantCount =
              (booking.adultsNumber || 0) + (booking.childrenNumber || 0);
            const initialApplicants = Array.from(
              { length: applicantCount },
              () => ({
                files: {
                  photo: undefined,
                  passportCopy: undefined,
                },
              })
            );
            form.reset({ applicants: initialApplicants });
          }
        }
      }
    };
    fetchBookingData();
  }, [id]);

  const onSubmit = async (data: BookingDocumentForm) => {
    if (!currentBooking) return;

    setIsLoading(true);
    const formData = new FormData();

    for (const applicant of data.applicants) {
      for (const [name, file] of Object.entries(applicant.files)) {
        let compressedFile = file;
        if (file.type.startsWith("image/")) {
          try {
            const compressedBlob = await imageCompression(file, {
              maxSizeMB: 0.2,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            });
            compressedFile = new File([compressedBlob], file.name, {
              type: compressedBlob.type,
            });
          } catch (error) {
            console.error("Compression error:", error);
          }
        }
        formData.append("Files", compressedFile);
      }
    }

    formData.append("directory", "seed/files/");
    formData.append("entityId", currentBooking.id);

    const result = await uploadImage(formData);
    setIsLoading(false);

    if (result.status === "success") {
      setFilesSubmitted(true);
      setAllFilesUploaded(true);
      setHasExistingFiles(true);
    }
  };

  if (!service)
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <Loader className="w-10 h-10 animate-spin text-primary" />
      </div>
    );

  const uploadedFilesCount = fields.reduce((total, _, applicantIndex) => {
    const applicantFiles = formValues.applicants?.[applicantIndex]?.files || {};

    // Type-safe way to count files
    let count = 0;
    if (applicantFiles.photo) count++;
    if (applicantFiles.passportCopy) count++;

    return total + count;
  }, 0);

  const totalRequiredFiles = fields.length * 2; // 2 files per applicant (photo + passportCopy)

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 custom-container ">
      <CheckoutPageAnimator />
      <Header />
      <main className="flex-grow pt-24 md:pt-32">
        <section id="checkout" className="pb-16">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left: Upload Form */}
              <div className="lg:col-span-2">
                <Card className="w-full shadow-md border-gray-200 bg-white rounded-xl">
                  <CardHeader>
                    <CardTitle className="text-2xl text-primary">
                      {hasExistingFiles ? "Documents Uploaded" : "Upload Required Documents"}
                    </CardTitle>
                    <CardDescription>
                      {hasExistingFiles ? (
                        "Your documents have been successfully uploaded and are under review."
                      ) : (
                        <>
                          Please upload a photo and passport copy for each applicant.
                          {totalRequiredFiles > 0 && (
                            <div className="mt-2 flex items-center gap-2 text-sm">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${(uploadedFilesCount / totalRequiredFiles) * 100}%`,
                                  }}
                                />
                              </div>
                              <span className="text-gray-600">
                                {uploadedFilesCount} / {totalRequiredFiles} files uploaded
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    {hasExistingFiles ? (
                      <div className="text-center py-8">
                        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          Documents Successfully Submitted
                        </h3>
                        <p className="text-gray-600 mb-4">
                          Your documents have been uploaded and are being processed. 
                          You can now proceed with the payment.
                        </p>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                          <p className="text-sm text-green-700">
                            <strong>Status:</strong> Documents submitted successfully
                          </p>
                        </div>
                      </div>
                    ) : (
                      <Form {...form}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                          {fields.map((field, applicantIndex) => (
                            <div
                              key={field.id}
                              className="border p-4 rounded-lg space-y-4 relative bg-gray-50/70 mb-6"
                            >
                              <div className="flex justify-between items-center">
                                <h3 className="font-semibold text-lg text-primary">
                                  Applicant {applicantIndex + 1}
                                </h3>
                              </div>
                              <Separator />
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FileUpload
                                  form={form}
                                  applicantIndex={applicantIndex}
                                  fieldName="photo"
                                  label="Photo *"
                                />
                                <FileUpload
                                  form={form}
                                  applicantIndex={applicantIndex}
                                  fieldName="passportCopy"
                                  label="Passport Copy *"
                                />
                              </div>
                            </div>
                          ))}

                          <div className="flex justify-end pt-4">
                            <Button
                              type="submit"
                              disabled={isLoading || filesSubmitted}
                              className="min-w-40"
                            >
                              {isLoading ? (
                                <Loader className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4 mr-2" />
                              )}
                              {isLoading
                                ? "Uploading..."
                                : filesSubmitted
                                  ? "Documents Submitted"
                                  : "Submit Documents"}
                            </Button>
                          </div>
                        </form>
                      </Form>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right: Booking Summary */}
              <div className="lg:col-span-1 sticky top-28">
                {hasExistingFiles ? (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">
                        Documents Ready!
                      </span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Your documents have been submitted. You can proceed with payment.
                    </p>
                  </div>
                ) : !allFilesUploaded && !filesSubmitted ? (
                  <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-700 mb-2">
                      <Loader className="w-5 h-5" />
                      <span className="font-semibold">Upload Required</span>
                    </div>
                    <p className="text-sm text-yellow-600">
                      Please upload photo and passport copy for all applicants to enable payment.
                    </p>
                    <div className="mt-2 text-xs text-yellow-700">
                      <p>
                        Progress: {uploadedFilesCount} / {totalRequiredFiles} files
                      </p>
                    </div>
                  </div>
                ) : allFilesUploaded && !filesSubmitted ? (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">All files uploaded!</span>
                    </div>
                    <p className="text-sm text-blue-600 mt-1">
                      Please submit the documents to proceed with payment.
                    </p>
                  </div>
                ) : (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">
                        Documents Submitted Successfully!
                      </span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      You can now proceed with payment.
                    </p>
                  </div>
                )}

                <BookingCheckout allFilesUploaded={hasExistingFiles || filesSubmitted} />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default observer(BookingDocument);